import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastComponent } from './main/shared/toast/toast.component';
import { ToastService } from './core/services/toast.service';
import { PermissionService } from './core/services/user/permission.service';
import { CallOverlayService } from './core/services/call-overlay/call-overlay.service';
import { AsyncPipe, CommonModule } from '@angular/common';
import { VideoCallModalComponent } from './shared/components/modal/video-call-modal/video-call-modal.component';
import { RotationWarningComponent } from './shared/components/rotation-warning/rotation-warning.component';
import { CallHubService } from './core/services/chat/call-hub.service';
import { AudioUnlockService } from './core/services/audio/audio-unlock.service';
import { ModalService } from './main/shared/services/modal.service';
import { CurrentUserService } from './core/services/auth/current-user.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    ToastComponent,
    AsyncPipe,
    VideoCallModalComponent,
    RotationWarningComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  toastMessage: string = '';
  toastType: 'success' | 'error' | 'warning' = 'success';
  showToast: boolean = false;

  private activeIncomingCallId: string | null = null;
  private currentRingtone: HTMLAudioElement | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    public callOverlay: CallOverlayService,
    private callHubService: CallHubService,
    private audioUnlock: AudioUnlockService,
    private appModalService: ModalService,
    private currentUserService: CurrentUserService,
  ) {
    this.translate.setDefaultLang('en-US');
  }

  ngOnInit(): void {
    this.toastService.toast$.subscribe((toast) => {
      this.toastMessage = toast.message;
      this.toastType = toast.type;
      this.showToast = toast.showToast;
    });

    this.currentUserService.onLogin$
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user?.id) {
          this.callHubService.ensureSignaling().catch(() => {});
        }
      });

    const existingUser = this.currentUserService.GetSession();
    if (existingUser?.id) {
      this.callHubService.ensureSignaling().catch(() => {});
    }

    this.setupGlobalCallHandlers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupGlobalCallHandlers(): void {
    this.callHubService
      .onIncomingCall()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (incoming) => {
        if (!incoming) return;
        const currentUserId = this.currentUserService.GetSession()?.id;
        
        if (currentUserId && incoming.fromUserId === currentUserId) {
          return;
        }

        if (this.activeIncomingCallId === incoming.callId) {
          console.warn(
            'Duplicate incoming call event ignored:',
            incoming.callId,
          );
          return;
        }

        if (this.currentRingtone && this.activeIncomingCallId) {
          this.currentRingtone.pause();
          this.currentRingtone.currentTime = 0;
          this.currentRingtone = null;
        }

        this.activeIncomingCallId = incoming.callId;

        const audio = new Audio('/assets/sounds/ringtone-027-376908.mp3');
        audio.loop = true;
        audio.preload = 'auto';
        audio.volume = 1.0;
        this.currentRingtone = audio;

        let notification: Notification | null = null;

        if (
          document.hidden &&
          'Notification' in window &&
          Notification.permission === 'granted'
        ) {
          try {
            const callerName =
              incoming.fromUserName ||
              this.translate.instant('video-call.unknown-user');
            const callTypeKey =
              incoming.callType === 2
                ? 'video-call.wants-video-call'
                : 'video-call.wants-audio-call';

            notification = new Notification(
              this.translate.instant('video-call.incoming-call'),
              {
                body: `${callerName} - ${this.translate.instant(callTypeKey)}`,
                icon: '/assets/media/logos/logo-light.png',
                badge: '/assets/media/logos/logo-light.png',
                tag: 'incoming-call',
                requireInteraction: true,
                silent: false,
              },
            );

            notification.onclick = () => {
              window.focus();
              notification?.close();
            };
          } catch (notifError) {
            console.warn('Failed to show notification:', notifError);
          }
        }

        const tryPlay = async () => {
          try {
            await audio.play();
          } catch (firstError) {
            await new Promise((resolve) => setTimeout(resolve, 150));

            try {
              await audio.play();
            } catch (secondError) {
              await this.audioUnlock.waitUntilUnlocked(15000).catch(() => {});
              try {
                await audio.play();
              } catch (finalError) {
                console.warn('All audio play attempts failed:', finalError);
              }
            }
          }
        };
        tryPlay();

        const modalRef = this.appModalService.openComponentModal(
          VideoCallModalComponent,
          {
            size: 'xl',
            centered: true,
            backdrop: 'static',
            keyboard: false,
          },
        );
        const comp = modalRef.componentInstance as VideoCallModalComponent;
        comp.incomingCall = incoming;
        comp.ringtoneAudio = audio;

        modalRef.closed.subscribe(() => {
          if (notification) {
            notification.close();
          }

          this.activeIncomingCallId = null;
          this.currentRingtone = null;
        });
      });
  }

  switchLanguage(lang: string) {
    this.translate.use(lang);
  }
}
